import React, { useMemo, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import DOMPurify from 'dompurify';
import Prism from 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
import { copyTextToClipboard } from '../../utils/clipboard';

// Curated language list for the composer's code-block picker (values match
// the Prism grammars imported above; mirrors LexicalRichEditor's set).
export const CODE_LANGUAGE_OPTIONS = [
  { value: 'plaintext', label: 'Plain text' },
  { value: 'json', label: 'JSON' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'jsx', label: 'JSX' },
  { value: 'tsx', label: 'TSX' },
  { value: 'markup', label: 'HTML / XML' },
  { value: 'css', label: 'CSS' },
  { value: 'bash', label: 'Bash / Shell' },
  { value: 'python', label: 'Python' },
  { value: 'sql', label: 'SQL' },
  { value: 'yaml', label: 'YAML' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'java', label: 'Java' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
];

// Best-effort language sniffing for code blocks sent without an explicit
// language (composer defaults to "Plain text"). Cheap regex signals only —
// good enough to light up the common cases instead of showing flat text.
export function detectLanguage(code) {
  const text = (code || '').trim();
  if (!text) return null;

  if (/^[{[]/.test(text)) {
    try {
      JSON.parse(text);
      return 'json';
    } catch {
      // not JSON, fall through
    }
  }
  if (
    /^#!.*\b(bash|sh|zsh)\b/.test(text) ||
    // `export` alone is ambiguous with shell (`export PATH=...`) and ESM
    // (`export default`/`export const`/`export * from`/`export { ... }`/...)
    // syntax — only treat it as a shell indicator when it's NOT immediately
    // followed by a JS/TS declaration keyword or a re-export `{`/`*`, so
    // `export default function Foo() {`, `export * from './utils'`, and
    // `export { foo, bar };` all still reach the javascript check below
    // instead of being misclassified.
    /^\s*(\$\s+)?(sudo|npm|yarn|pnpm|git|curl|cd|ls|echo|export(?!\s+(default|const|let|var|class|function|async|type|interface)\b)(?!\s*[{*])|brew|docker|kubectl)\s/.test(
      text
    )
  ) {
    return 'bash';
  }
  if (/^\s*(SELECT|INSERT\s+INTO|UPDATE|DELETE\s+FROM|CREATE\s+TABLE|ALTER\s+TABLE|DROP\s+TABLE|WITH)\b/i.test(text)) {
    return 'sql';
  }
  if (/^<([a-zA-Z!])/.test(text) && (/<\/[a-zA-Z]+>/.test(text) || /\/>/.test(text))) {
    return 'markup';
  }
  // A single "word: value" line is too common in plain prose ("Note: check
  // this out") to be a reliable signal on its own — require at least two
  // such lines (an actual key list) unless there's an explicit `---` doc marker.
  const yamlKeyLines = (text.match(/^\s*[\w.-]+:\s/gm) || []).length;
  if (/^---/.test(text) || (yamlKeyLines >= 2 && !/[;{}]/.test(text))) {
    return 'yaml';
  }
  if (/\bfn\s+\w+\s*\(/.test(text) && (/->/.test(text) || /let\s+mut\b/.test(text) || /::/.test(text))) {
    return 'rust';
  }
  if (/^\s*package\s+\w+/m.test(text) || (/\bfunc\s+\w*\s*\(/.test(text) && /:=/.test(text))) {
    return 'go';
  }
  if (/\bpublic\s+(class|static)\b/.test(text) && /System\.out\.println/.test(text)) {
    return 'java';
  }
  if (/^\s*(def|class)\s+\w+.*:\s*$/m.test(text) || (/^\s*import\s+\w+/m.test(text) && /:\s*$/m.test(text))) {
    return 'python';
  }
  const hasJsxTag = /<[A-Za-z][\w.]*[^>]*>/.test(text) && /<\/[A-Za-z]/.test(text);
  const hasTsTypes = /:\s*(string|number|boolean|any|void|React\.\w+)\b/.test(text) || /\binterface\s+\w+/.test(text);
  if (hasJsxTag) return hasTsTypes ? 'tsx' : 'jsx';
  if (hasTsTypes) return 'typescript';
  if (/[.#][\w-]+\s*\{[\s\S]*:[\s\S]*\}/.test(text)) return 'css';
  if (
    /\b(function|const|let|var|class)\s+\w+|=>|console\.log|require\(|module\.exports|\bexport\b/.test(
      text
    )
  ) {
    return 'javascript';
  }
  if (/^#{1,6}\s+\S/m.test(text) || /^\s*[-*]\s+\S/m.test(text)) return 'markdown';
  return null;
}

// Notion-style code block: subtle surface background (matches the reaction
// pills, not a hardcoded dark-navy block), an optional language badge, and a
// copy button that reveals in the top-right corner on hover.
export function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);
  const effectiveLanguage = useMemo(
    () => (language && language !== 'plaintext' ? language : detectLanguage(code)),
    [code, language]
  );
  const grammar = effectiveLanguage ? Prism.languages[effectiveLanguage] : null;
  const html = useMemo(() => {
    if (!grammar) return null;
    const highlighted = Prism.highlight(code || '', grammar, effectiveLanguage);
    // Prism only ever emits <span class="token ...">-wrapped text, but the
    // input is chat message content — sanitize defensively rather than trust
    // Prism's own escaping to hold against every grammar/edge case.
    return DOMPurify.sanitize(highlighted, { ALLOWED_TAGS: ['span'], ALLOWED_ATTR: ['class'] });
  }, [code, grammar, effectiveLanguage]);

  const onCopy = async () => {
    const ok = await copyTextToClipboard(code || '');
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className={`chat-code-block${copied ? ' is-copied' : ''}`}>
      <pre className="chat-code" data-language={grammar ? effectiveLanguage : undefined}>
        {html ? <code dangerouslySetInnerHTML={{ __html: html }} /> : <code>{code}</code>}
      </pre>
      <button type="button" className="chat-code-copy-btn" onClick={onCopy} title="Copy code">
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
}
